import React from 'react';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { ContainedButton } from '../../components/Main/Landing/components/buttons/Buttons';
import publicStyles from '../../layouts/PublicLayout.module.css';

import travelDiariesImg from "../../components/Main/Landing/images/travel-diaries.png";
import fineDiningImg from "../../components/Main/Landing/images/fine-dining.png";
import fashionStyleImg from "../../components/Main/Landing/images/fashion-style.png";
import exclusiveEventsImg from "../../components/Main/Landing/images/exclusive-events.png";
import behindTheScenesImg from "../../components/Main/Landing/images/behind-the-scenes.png";
import lifestyleInspirationImg from "../../components/Main/Landing/images/lifestyle-inspiration.png";

const useStyles = makeStyles((theme) => ({
  section: {
    maxWidth: '1314px',
    margin: '0 auto',
    padding: '0 21px',
    textAlign: 'center',
    marginBottom: '45px',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '36px',
    }
  },
  sectionTitle: {
    fontFamily: '"Cormorant Garamond", serif',
    fontSize: '52px',
    fontWeight: 400,
    lineHeight: '100%',
    marginBottom: '16px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '36px',
    }
  },
  sectionSubtitle: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '18px',
    color: '#FFFFFF',
    lineHeight: '27px',
    fontWeight: 200,
    marginBottom: '42px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
      lineHeight: '22px',
      margin: '0  auto 32px auto',
      maxWidth: "90%",
      fontWeight: 200,
    }
  },
  mobileOnlyText: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'inline',
    }
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '32px',
    marginBottom: '48px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '32px',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: '44px',
    }
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '418px',
    width: '100%',
    margin: '0 auto',
    cursor: 'pointer',
    '&:hover $cardImage': {
      transform: 'scale(1.1)',
    },
    '&:hover $cardDesc': {
      opacity: 1,
      maxHeight: '100px',
      transform: 'translateY(0)',
      marginTop: '6px',
    },
    [theme.breakpoints.down('sm')]: {
      maxWidth: '348px',
    }
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    height: '280px',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: '#111111',
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.4s ease',
  },
  cardOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '24px 20px',
    background: 'linear-gradient(180deg, rgba(17, 17, 17, 0) 0%, rgba(17, 17, 17, 0.75) 50%, rgba(17, 17, 17, 0.95) 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    zIndex: 1,
  },
  cardTitle: {
    fontFamily: '"Cormorant Garamond", serif',
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: "100%",
    color: '#ffffff',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px'
    }
  },
  cardDesc: {
    fontFamily: '"Roboto", sans-serif',
    fontSize: '18px',
    fontWeight: 200,
    color: '#FFFFFF',
    lineHeight: '27px',
    textAlign: 'left',
    opacity: 0,
    maxHeight: 0,
    transform: 'translateY(12px)',
    transition: 'all 0.35s ease',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      opacity: 1,
      maxHeight: '100px',
      transform: 'translateY(0)',
      marginTop: '6px',
      fontSize: '16px',
      lineHeight: '24px',
    }
  },
  btnWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '16px',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    }
  },
  fullWidthBtn: {
    [theme.breakpoints.down('sm')]: {
      width: '100% !important',
      maxWidth: '100% !important',
      height: '38px',
      padding: '16px 32px',
      borderRadius: '6px !important',
    }
  }
}));

const EXPERIENCES = [
  {
    title: "Travel Diaries",
    description: "Private moments from destinations around the world, told first-person.",
    image: travelDiariesImg
  },
  {
    title: "Fine Dining",
    description: "Tasting menus, private chefs, and the world's most exclusive tables.",
    image: fineDiningImg
  },
  {
    title: "Fashion & Style",
    description: "Editorial looks and personal style, shaped by creators who live it daily.",
    image: fashionStyleImg
  },
  {
    title: "Exclusive Events",
    description: "Front-row seats to launches, galas, and gatherings closed to the public.",
    image: exclusiveEventsImg
  },
  {
    title: "Behind the Scenes",
    description: "The unfiltered side of the lifestyle — what happens before the shot.",
    image: behindTheScenesImg
  },
  {
    title: "Lifestyle Inspiration",
    description: "Ideas, routines, and taste from creators who set the standard.",
    image: lifestyleInspirationImg
  }
];

export const LifestyleExperiencesSection = () => {
  const classes = useStyles();
  const history = useHistory();

  return (
    <Box className={classes.section}>
      <Typography className={classes.sectionTitle}>
        Explore Exclusive Experiences
      </Typography>
      <Typography className={classes.sectionSubtitle}>
        A window into the world our creators live and share<span className={classes.mobileOnlyText}> — reserved for verified members.</span>
      </Typography>

      <Box className={classes.grid}>
        {EXPERIENCES.map((exp, index) => (
          <Box key={index} className={classes.card}>
            <Box className={classes.imageWrapper}>
              <img src={exp.image} alt={exp.title} className={classes.cardImage} />
              <Box className={classes.cardOverlay}>
                <Typography className={classes.cardTitle}>
                  {exp.title}
                </Typography>
                {exp.description && (
                  <Typography className={classes.cardDesc}>
                    {exp.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      <Box className={classes.btnWrapper}>
        <ContainedButton text="Request Member Access" styles={`${publicStyles.roundedBtn} ${classes.fullWidthBtn}`} onClick={() => history.push('/member-onboarding-step')} />
      </Box>
    </Box>
  );
};
